import { memo, useRef, useState, type CSSProperties } from "react";

export const OTP_LENGTH = 6;

export const normalizeOtpCode = (raw: string): string =>
  raw.replace(/\D/g, "").slice(0, OTP_LENGTH);

export type OtpCodeInputProps = {
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  textColor: string;
  /** Theme CTA gradient (`backgroundBtn`) for the active cell border. */
  borderGradient: string;
  /** Card fill so the gradient border mask stays a 2px ring. */
  fillColor: string;
  invalid?: boolean;
  errorColor?: string;
  ariaLabel: string;
  /** Paint the active cell without real focus, for static renders. */
  forceActive?: boolean;
};

/**
 * Six visual cells over a single numeric input so SMS autofill, paste, and
 * typing all land on one field. Clerk still receives the concatenated string.
 */
export const OtpCodeInput = memo(
  ({
    value,
    onChange,
    disabled = false,
    textColor,
    borderGradient,
    fillColor,
    invalid = false,
    errorColor,
    ariaLabel,
    forceActive = false,
  }: OtpCodeInputProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [focused, setFocused] = useState(false);
    const [caret, setCaret] = useState(0);
    const digits = normalizeOtpCode(value);
    const restingIndex =
      digits.length === OTP_LENGTH ? OTP_LENGTH - 1 : digits.length;
    // While editing the caret leads on typing and trails on delete, so erasing
    // a full code marks the digit that survived, not the slot it left empty.
    const activeIndex = focused ? caret : restingIndex;
    const showActive = (focused || forceActive) && !disabled;

    const handleChange = (raw: string) => {
      const next = normalizeOtpCode(raw);
      setCaret(
        next.length < digits.length
          ? Math.max(0, next.length - 1)
          : Math.min(next.length, OTP_LENGTH - 1),
      );
      onChange(next);
    };

    return (
      <div
        className={`otp-code${invalid ? " is-invalid" : ""}`}
        style={
          {
            color: textColor,
            "--otp-active-border": borderGradient,
            "--otp-cell-fill": fillColor,
            "--otp-error-border": errorColor ?? "#e81c1c",
          } as CSSProperties
        }
        onClick={() => inputRef.current?.focus()}
      >
        <input
          ref={inputRef}
          className="otp-code__input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={digits}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={invalid}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => {
            setFocused(true);
            setCaret(Math.min(digits.length, OTP_LENGTH - 1));
          }}
          onBlur={() => setFocused(false)}
        />
        <div className="otp-code__cells" aria-hidden>
          {Array.from({ length: OTP_LENGTH }, (_, index) => {
            const filled = index < digits.length;
            const active = showActive && index === activeIndex;
            return (
              <span
                key={index}
                className={`otp-code__cell${filled ? " is-filled" : ""}${
                  active ? " is-active" : ""
                }`}
              >
                {digits[index] ?? ""}
              </span>
            );
          })}
        </div>
      </div>
    );
  }
);
